import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import {
    FaChevronDown, FaChevronUp, FaPlus, FaTrash, FaShieldAlt, FaAward
} from "react-icons/fa";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import styles from "./SifatSiyosati.module.css";
import { testUrl } from "../home/BaseData";

const SifatSiyosati = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [activeIndex, setActiveIndex] = useState(null);
    const [policyData, setPolicyData] = useState([]);
    const [introData, setIntroData] = useState({ content_uz: "" });
    const [loading, setLoading] = useState(true);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [presentations, setPresentations] = useState([]);
    const [selectedImage, setSelectedImage] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [sdCodes, setSdCodes] = useState([]);

    // Translation keys for the page
    const translations = {
        uz: {
            mainTitle: "Universitetning ichki taʼlim sifatini taʼminlash siyosati",
            seeMore: "Ko'proq ko'rish",
            close: "Yopish",
            loading: "Yuklanmoqda...",
            documentsTitle: "Sifat siyosatiga doir hujjatlar",
            sdCodesTitle: "SD Kodlari ro'yxati",
            viewMoreSD: "Barcha SD kodlarini ko'rish",
            noDocuments: "Hujjatlar topilmadi",
        },
        en: {
            mainTitle: "University's Internal Education Quality Assurance Policy",
            seeMore: "See more",
            close: "Close",
            loading: "Loading...",
            documentsTitle: "Quality Policy Documents",
            sdCodesTitle: "SD Codes List",
            viewMoreSD: "View all SD codes",
            noDocuments: "No documents found",
        },
        ru: {
            mainTitle: "Политика обеспечения внутреннего качества образования университета",
            seeMore: "Подробнее",
            close: "Закрыть",
            loading: "Загрузка...",
            documentsTitle: "Документы политики качества",
            sdCodesTitle: "Список кодов SD",
            viewMoreSD: "Посмотреть все коды SD",
            noDocuments: "Документы не найдены",
        }
    };

    const currentLang = i18n.language || 'uz';
    const texts = translations[currentLang] || translations.uz;


    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
            ['link', 'clean']
        ],
    };

    const initialSections = [
        {
            title: t("quality_policy_general_provisions"),
            content: `<p>${t("quality_policy_1_1")}</p><p>${t("quality_policy_1_2")}</p>`
        },
        {
            title: t("quality_policy_section_2"),
            content: `<p>${t("quality_policy_11_principles")}</p>`
        },
        {
            title: t("quality_policy_section_3"),
            content: `<p>${t("quality_policy_responsibility_structure")}</p>`
        },
        {
            title: t("quality_policy_section_4"),
            content: `<p>${t("quality_policy_periodic_eval")}</p><p>${t("quality_policy_public_open")}</p>`
        }
    ];

    const initialIntro = t("quality_policy_intro");

    useEffect(() => {
        fetchData();
    }, [i18n.language]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${testUrl}/api/quality-policy/`);

            if (response.data) {
                const { sections, intro, presentations, card_images, documents, sd_codes } = response.data;
                const lang = i18n.language;

                if (sections && sections.length > 0) {
                    const mappedData = sections.map(item => ({
                        ...item,
                        title: item[`title_${lang}`] || item.title_uz,
                        content: item[`content_${lang}`] || item.content_uz
                    }));
                    setPolicyData(mappedData);
                } else {
                    setPolicyData(initialSections);
                }

                if (intro) {
                    setIntroData({
                        ...intro,
                        content: intro[`content_${lang}`] || intro.content_uz,
                        content_uz: intro.content_uz
                    });
                } else {
                    setIntroData({ content: initialIntro, content_uz: initialIntro });
                }

                if (presentations) setPresentations(presentations);
                if (documents) setDocuments(documents);
                if (sd_codes) setSdCodes(sd_codes);
            } else {
                setPolicyData(initialSections);
                setIntroData({ content: initialIntro, content_uz: initialIntro });
            }
        } catch (error) {
            console.error("Error fetching quality policy:", error);
            setPolicyData(initialSections);
            setIntroData({ content: initialIntro, content_uz: initialIntro });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [i18n.language]);


    const toggleAccordion = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    const handleSave = async () => {
        try {
            const authToken = localStorage.getItem("authToken");
            const payload = {
                sections: policyData.map(item => ({
                    ...item,
                    // If we are editing specifically title and content in the UI, 
                    // ensure we map them back to the state fields expected by the backend
                    title_uz: item.title_uz || item.title,
                    content_uz: item.content_uz || item.content,
                })),
                intro: {
                    ...introData,
                    content_uz: introData.content_uz
                }
            };
            const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
            await axios.post(`${testUrl}/api/quality-policy/bulk-sync/`, payload, { headers });
            setIsEditModalOpen(false);
            alert("Ma'lumotlar global miqyosda saqlandi!");
            fetchData();
        } catch (error) {
            console.error("Error saving quality policy:", error);
            alert("Xatolik yuz berdi. Iltimos qaytadan urunib ko'ring.");
        }
    };

    const addNewSection = () => {
        setPolicyData([...policyData, { title: "Yangi bo'lim", content: "" }]);
    };

    const deleteSection = (index) => {
        if (window.confirm("Haqiqatan ham ushbu bo'limni o'chirib tashlamoqchimisiz?")) {
            const newData = policyData.filter((_, i) => i !== index);
            setPolicyData(newData);
        }
    };

    const updateSection = (index, field, value) => {
        const newData = [...policyData];
        newData[index][field] = value;
        setPolicyData(newData);
    };

    const updateIntro = (value) => {
        setIntroData({ ...introData, content: value, content_uz: value });
    };

    if (loading) {
        return <div className={styles.loading}>{texts.loading}</div>;
    }

    return (
        <div className={styles.mainContainer}>
            <div className={styles.container}>
                <h1 className={styles.mainTitle} data-aos="fade-up">
                    {texts.mainTitle}
                </h1>

                {/* Decorative Divider */}
                <div className={styles.premiumDivider} data-aos="fade-up">
                    <div className={styles.dividerLine}></div>
                    <div className={styles.dividerIcon}>
                        <FaAward />
                    </div>
                    <div className={styles.dividerLine}></div>
                </div>

                <div className={styles.introBox} data-aos="fade-up">
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(introData.content || initialIntro, { ADD_ATTR: ['style', 'target'] }) }} />
                </div>

                {localStorage.getItem("authToken") && (
                    <div className={styles.adminControls}>
                        <button className={styles.adminEditBtn} onClick={() => setIsEditModalOpen(true)}>
                            <FaPlus style={{ marginRight: '8px' }} /> Sifat siyosatini tahrirlash (Admin)
                        </button>
                    </div>
                )}


                <div className={styles.accordionContainer} data-aos="fade-up">
                    {policyData.map((item, index) => (
                        <div key={index} className={`${styles.accordionItem} ${activeIndex === index ? styles.active : ""}`}>
                            <button className={styles.accordionHeader} onClick={() => toggleAccordion(index)}>
                                <div className={styles.headerTitle}>
                                    <span className={styles.icon}>
                                        <FaShieldAlt />
                                    </span>
                                    <h2>{item.title}</h2>
                                </div>
                                <div className={styles.headerAction}>
                                    <span className={styles.seeMoreTxt}>{activeIndex === index ? texts.close : texts.seeMore}</span>
                                    {activeIndex === index ? <FaChevronUp /> : <FaChevronDown />}
                                </div>
                            </button>
                            <div className={styles.accordionContent}>
                                <div className={styles.innerContent}>
                                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.content, { ADD_ATTR: ['style', 'target'] }) }} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* NEW: Documents List After Accordion */}
                {documents && documents.length > 0 && (
                    <div className={styles.accordionItem} style={{ border: 'none', background: 'transparent', marginTop: '40px' }} data-aos="fade-up">
                        <h2 style={{ fontSize: '24px', color: '#1a365d', marginBottom: '20px', fontWeight: '700', textAlign: 'center' }}>{texts.documentsTitle}</h2>
                        <div className={styles.docListGrid}>
                            {documents.map((doc, idx) => (
                                <a
                                    key={doc.id}
                                    href={doc.document}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.docCardLink}
                                >
                                    <div className={styles.docIcon}>📄</div>
                                    <div className={styles.docInfo}>
                                        <span className={styles.docName}>{idx + 1}. {doc[`name_${currentLang}`] || doc.name_uz || "Unnamed Document"}</span>
                                    </div>
                                </a>
                            ))}
                        </div>
                    </div>
                )}



                {/* SIFAT SIYOSATI KO'RGAZMALI KARTOCHKALARI - Statik */}
                <div className={styles.cardsGrid} data-aos="fade-up">
                    {/* CARD 1: Asosiy tushunchalar */}
                    <div className={`${styles.card} ${styles.wideCard}`}>
                        <div className={`${styles.cardStrip} ${styles.navyStrip}`}></div>
                        <div className={styles.cardHeader}>
                            <div className={`${styles.cardIcon} ${styles.navyLight}`}>📖</div>
                            <div className={styles.cardTitleArea}>
                                <div className={`${styles.cardSection} ${styles.navyText}`}>{t("quality_policy_section_1")}</div>
                                <div className={styles.cardTitle}>{t("quality_policy_basic_concepts")}</div>
                            </div>
                        </div>
                        <div className={styles.cardBody}>
                            <ul className={styles.itemList}>
                                <li>
                                    <span className={`${styles.dot} ${styles.navyDot}`}>1</span>
                                    <div><strong>{t("Ta'lim sifati")}</strong><div style={{ marginTop: "2px" }}>{t("quality_policy_concept_1_desc")}</div></div>
                                </li>
                                <li>
                                    <span className={`${styles.dot} ${styles.blueDot}`}>2</span>
                                    <div><strong>{t("Sifatni ta'minlash")}</strong><div style={{ marginTop: "2px" }}>{t("quality_policy_concept_2_desc")}</div></div>
                                </li>
                                <li>
                                    <span className={`${styles.dot} ${styles.tealDot}`}>3</span>
                                    <div><strong>{t("Ichki sifat tizimi")}</strong><div style={{ marginTop: "2px" }}>{t("quality_policy_concept_3_desc")}</div></div>
                                </li>
                                <li>
                                    <span className={`${styles.dot} ${styles.goldDot}`}>4</span>
                                    <div><strong>{t("Sifat madaniyati")}</strong><div style={{ marginTop: "2px" }}>{t("quality_policy_concept_4_desc")}</div></div>
                                </li>
                                <li>
                                    <span className={`${styles.dot} ${styles.purpleDot}`}>5</span>
                                    <div><strong>{t("Manfaatdorlar")}</strong><div style={{ marginTop: "2px" }}>{t("quality_policy_concept_5_desc")}</div></div>
                                </li>
                                <li>
                                    <span className={`${styles.dot} ${styles.greenDot}`}>6</span>
                                    <div><strong>{t("Siyosat yo'nalishlari (SY)")}</strong><div style={{ marginTop: "2px" }}>{t("quality_policy_concept_6_desc")}</div></div>
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* CARD 2: 11 Tamoyil */}
                    <div className={`${styles.card} ${styles.wideCard}`}>
                        <div className={`${styles.cardStrip} ${styles.tealStrip}`}></div>
                        <div className={styles.cardHeader}>
                            <div className={`${styles.cardIcon} ${styles.goldLight}`}>⚖️</div>
                            <div className={styles.cardTitleArea}>
                                <div className={`${styles.cardSection} ${styles.tealText}`}>{t("quality_policy_section_2")}</div>
                                <div className={styles.cardTitle}>{t("quality_policy_11_principles")}</div>
                            </div>
                        </div>
                        <div className={styles.cardBody}>
                            <div className={styles.miniGrid}>
                                <div className={styles.miniCard} style={{ border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                                    <div className={`${styles.dot} ${styles.navyDot}`} style={{ marginBottom: 8, width: 20, height: 20, fontSize: 10 }}>1</div>
                                    <strong>{t("quality_policy_principle_1_title")}</strong>
                                    <span style={{ fontSize: 12, color: "var(--gray)", display: "block" }}>{t("quality_policy_principle_1_desc")}</span>
                                </div>
                                <div className={styles.miniCard} style={{ border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                                    <div className={`${styles.dot} ${styles.blueDot}`} style={{ marginBottom: 8, width: 20, height: 20, fontSize: 10 }}>2</div>
                                    <strong>{t("quality_policy_principle_2_title")}</strong>
                                    <span style={{ fontSize: 12, color: "var(--gray)", display: "block" }}>{t("quality_policy_principle_2_desc")}</span>
                                </div>
                                <div className={styles.miniCard} style={{ border: "1px solid #e2e8f0", background: "#f0fdfa" }}>
                                    <div className={`${styles.dot} ${styles.tealDot}`} style={{ marginBottom: 8, width: 20, height: 20, fontSize: 10 }}>3</div>
                                    <strong>{t("quality_policy_principle_3_title")}</strong>
                                    <span style={{ fontSize: 12, color: "var(--gray)", display: "block" }}>{t("quality_policy_principle_3_desc")}</span>
                                </div>
                                <div className={styles.miniCard} style={{ border: "1px solid #e2e8f0", background: "#f5f3ff" }}>
                                    <div className={`${styles.dot} ${styles.purpleDot}`} style={{ marginBottom: 8, width: 20, height: 20, fontSize: 10 }}>4</div>
                                    <strong>{t("quality_policy_principle_4_title")}</strong>
                                    <span style={{ fontSize: 12, color: "var(--gray)", display: "block" }}>{t("quality_policy_principle_4_desc")}</span>
                                </div>
                                <div className={styles.miniCard} style={{ border: "1px solid #e2e8f0", background: "#fefce8" }}>
                                    <div className={`${styles.dot} ${styles.goldDot}`} style={{ marginBottom: 8, width: 20, height: 20, fontSize: 10 }}>5</div>
                                    <strong>{t("quality_policy_principle_5_title")}</strong>
                                    <span style={{ fontSize: 12, color: "var(--gray)", display: "block" }}>{t("quality_policy_principle_5_desc")}</span>
                                </div>
                                <div className={styles.miniCard} style={{ border: "1px solid #e2e8f0", background: "#f0fdf4" }}>
                                    <div className={`${styles.dot} ${styles.greenDot}`} style={{ marginBottom: 8, width: 20, height: 20, fontSize: 10 }}>6</div>
                                    <strong>{t("quality_policy_principle_6_title")}</strong>
                                    <span style={{ fontSize: 12, color: "var(--gray)", display: "block" }}>{t("quality_policy_principle_6_desc")}</span>
                                </div>
                                <div className={styles.miniCard} style={{ border: "1px solid #e2e8f0", background: "#fff7ed" }}>
                                    <div className={`${styles.dot} ${styles.orangeDot}`} style={{ marginBottom: 8, width: 20, height: 20, fontSize: 10 }}>7</div>
                                    <strong>{t("quality_policy_principle_7_title")}</strong>
                                    <span style={{ fontSize: 12, color: "var(--gray)", display: "block" }}>{t("quality_policy_principle_7_desc")}</span>
                                </div>
                                <div className={styles.miniCard} style={{ border: "1px solid #e2e8f0", background: "#f0fdfa" }}>
                                    <div className={`${styles.dot} ${styles.tealDot}`} style={{ marginBottom: 8, width: 20, height: 20, fontSize: 10 }}>8</div>
                                    <strong>{t("quality_policy_principle_8_title")}</strong>
                                    <span style={{ fontSize: 12, color: "var(--gray)", display: "block" }}>{t("quality_policy_principle_8_desc")}</span>
                                </div>
                                <div className={styles.miniCard} style={{ border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                                    <div className={`${styles.dot} ${styles.navyDot}`} style={{ marginBottom: 8, width: 20, height: 20, fontSize: 10 }}>9</div>
                                    <strong>{t("quality_policy_principle_9_title")}</strong>
                                    <span style={{ fontSize: 12, color: "var(--gray)", display: "block" }}>{t("quality_policy_principle_9_desc")}</span>
                                </div>
                                <div className={styles.miniCard} style={{ border: "1px solid #e2e8f0", background: "#fefce8" }}>
                                    <div className={`${styles.dot} ${styles.goldDot}`} style={{ marginBottom: 8, width: 20, height: 20, fontSize: 10 }}>10</div>
                                    <strong>{t("quality_policy_principle_10_title")}</strong>
                                    <span style={{ fontSize: 12, color: "var(--gray)", display: "block" }}>{t("quality_policy_principle_10_desc")}</span>
                                </div>
                                <div className={styles.miniCard} style={{ border: "1px solid #e2e8f0", background: "#f8fafc", gridColumn: "1 / -1" }}>
                                    <div className={`${styles.dot} ${styles.navyDot}`} style={{ marginBottom: 8, width: 20, height: 20, fontSize: 10 }}>11</div>
                                    <strong>{t("quality_policy_principle_11_title")}</strong>
                                    <span style={{ fontSize: 12, color: "var(--gray)", display: "block" }}>{t("quality_policy_principle_11_desc")}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CARD 3: Javobgarlik */}
                    <div className={`${styles.card} ${styles.wideCard}`}>
                        <div className={`${styles.cardStrip} ${styles.blueStrip}`}></div>
                        <div className={styles.cardHeader}>
                            <div className={`${styles.cardIcon}`} style={{ background: "#f1f5f9", boxShadow: "inset 0 0 0 1px #e2e8f0" }}>🏛️</div>
                            <div className={styles.cardTitleArea}>
                                <div className={`${styles.cardSection} ${styles.blueText}`}>{t("quality_policy_section_3")}</div>
                                <div className={styles.cardTitle}>{t("quality_policy_responsibility_structure")}</div>
                            </div>
                        </div>
                        <div className={styles.cardBody}>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "10px", marginBottom: "10px" }}>
                                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                    <div className={styles.roleRow}>
                                        <div className={`${styles.roleBar} ${styles.navyBg}`}></div>
                                        <div className={styles.roleContent}><strong>{t("quality_policy_rahbariyat")}</strong><span>{t("quality_policy_rahbariyat_desc")} <br /><i style={{ opacity: 0.8, marginTop: 4, display: "block", fontSize: 11.5 }}>{t("quality_policy_rahbariyat_detail")}</i></span></div>
                                    </div>
                                    <div className={styles.roleRow}>
                                        <div className={`${styles.roleBar} ${styles.blueBg}`}></div>
                                        <div className={styles.roleContent}><strong>{t("quality_policy_prorektorlar")}</strong><span>{t("quality_policy_prorektorlar_desc")}</span></div>
                                    </div>
                                    <div className={styles.roleRow}>
                                        <div className={`${styles.roleBar} ${styles.tealBg}`}></div>
                                        <div className={styles.roleContent}><strong>{t("quality_policy_fakultetlar")}</strong><span>{t("quality_policy_fakultetlar_desc")}</span></div>
                                    </div>
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                    <div className={styles.roleRow}>
                                        <div className={`${styles.roleBar} ${styles.purpleBg}`}></div>
                                        <div className={styles.roleContent}><strong>{t("quality_policy_sifat_bolinmasi")}</strong><span>{t("quality_policy_sifat_bolinmasi_desc")}</span></div>
                                    </div>
                                    <div className={styles.roleRow}>
                                        <div className={`${styles.roleBar} ${styles.orangeBg}`}></div>
                                        <div className={styles.roleContent}><strong>{t("quality_policy_professor_xodimlar")}</strong><span>{t("quality_policy_professor_xodimlar_desc")}</span></div>
                                    </div>
                                    <div className={styles.roleRow}>
                                        <div className={`${styles.roleBar} ${styles.greenBg}`}></div>
                                        <div className={styles.roleContent}><strong>{t("quality_policy_talabalar")}</strong><span>{t("quality_policy_talabalar_desc")}</span></div>
                                    </div>
                                </div>
                            </div>
                            <div style={{ padding: "14px 18px", backgroundColor: "#f1f5f9", borderRadius: "10px", fontSize: "12.5px", color: "var(--gray)", lineHeight: 1.5 }}>
                                {t("quality_policy_report_control")}
                            </div>
                        </div>
                    </div>

                    {/* CARD 4: SY1 Sifat Siyosati */}
                    <div className={styles.card}>
                        <div className={`${styles.cardStrip} ${styles.goldStrip}`}></div>
                        <div className={styles.cardHeader}>
                            <div className={`${styles.cardIcon} ${styles.goldLight}`}>📋</div>
                            <div className={styles.cardTitleArea}>
                                <div className={`${styles.cardSection} ${styles.goldText}`}>{t("quality_policy_section_4")}</div>
                                <div className={styles.cardTitle}>{t("quality_policy_periodic_eval")}</div>
                            </div>
                        </div>
                        <div className={styles.cardBody}>
                            <div className={`${styles.highlightInfo}`} style={{ background: "#fefce8", color: "var(--gray)", border: "1px solid #fef08a", marginBottom: "16px" }}>
                                {t("quality_policy_public_open")}
                            </div>
                            <p style={{ fontSize: "12.5px", fontWeight: "700", color: "var(--gray)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "10px" }}>{t("quality_policy_qamrovi")}</p>
                            <ul className={styles.itemList} style={{ marginBottom: "16px", gap: "8px" }}>
                                <li><span className={`${styles.dot} ${styles.goldDot}`} style={{ borderRadius: "50%", color: "#fff", background: "#d97706" }}>✓</span><div style={{ alignSelf: "center", fontSize: "13px" }}>{t("quality_policy_qamrovi_item_1")}</div></li>
                                <li><span className={`${styles.dot} ${styles.goldDot}`} style={{ borderRadius: "50%", color: "#fff", background: "#d97706" }}>✓</span><div style={{ alignSelf: "center", fontSize: "13px" }}>{t("quality_policy_qamrovi_item_2")}</div></li>
                                <li><span className={`${styles.dot} ${styles.goldDot}`} style={{ borderRadius: "50%", color: "#fff", background: "#d97706" }}>✓</span><div style={{ alignSelf: "center", fontSize: "13px" }}>{t("quality_policy_qamrovi_item_3")}</div></li>
                                <li><span className={`${styles.dot} ${styles.goldDot}`} style={{ borderRadius: "50%", color: "#fff", background: "#d97706" }}>✓</span><div style={{ alignSelf: "center", fontSize: "13px" }}>{t("quality_policy_qamrovi_item_4")}</div></li>
                                <li><span className={`${styles.dot} ${styles.goldDot}`} style={{ borderRadius: "50%", color: "#fff", background: "#d97706" }}>✓</span><div style={{ alignSelf: "center", fontSize: "13px" }}>{t("quality_policy_qamrovi_item_5")}</div></li>
                            </ul>
                            <p style={{ fontSize: "12.5px", fontWeight: "700", color: "var(--gray)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "10px" }}>{t("quality_policy_external_eval")}</p>
                            <div className={styles.badgeRow}>
                                <span className={`${styles.badgeItem} ${styles.navyLight}`}>{t("quality_policy_litsenziyalash")}</span>
                                <span className={`${styles.badgeItem} ${styles.tealLight}`}>{t("quality_policy_inst_akkred")}</span>
                                <span className={`${styles.badgeItem} ${styles.blueLight}`}>{t("quality_policy_spec_akkred")}</span>
                                <span className={`${styles.badgeItem} ${styles.purpleLight}`}>{t("quality_policy_otm_reyting")}</span>
                                <span className={`${styles.badgeItem} ${styles.goldLight}`}>{t("quality_policy_td_reyting")}</span>
                            </div>
                        </div>
                    </div>

                    {/* CARD 5: SY2 Ta'lim Dasturlari */}
                    <div className={styles.card}>
                        <div className={`${styles.cardStrip} ${styles.purpleStrip}`}></div>
                        <div className={styles.cardHeader}>
                            <div className={`${styles.cardIcon} ${styles.purpleLight}`} style={{ background: "#f3e8ff", color: "#7e22ce" }}>🔄</div>
                            <div className={styles.cardTitleArea}>
                                <div className={styles.cardSection} style={{ color: "var(--purple)" }}>{t("quality_policy_section_5")}</div>
                                <div className={styles.cardTitle}>{t("quality_policy_td_monitoring")}</div>
                            </div>
                        </div>
                        <div className={styles.cardBody}>
                            <div className={`${styles.highlightInfo}`} style={{ background: "#f3e8ff", color: "var(--purple)", marginBottom: "20px" }}>
                                {t("quality_policy_pdca_desc")}
                            </div>
                            <ul className={styles.itemList} style={{ gap: "14px", marginBottom: "20px" }}>
                                <li>
                                    <span className={`${styles.dot}`} style={{ background: "#1e293b", color: "#fff" }}>1</span>
                                    <div><strong>{t("quality_policy_pdca_1_title")}</strong><div style={{ marginTop: "2px", color: "var(--gray)", fontSize: "13px" }}>{t("quality_policy_pdca_1_desc")}</div></div>
                                </li>
                                <li>
                                    <span className={`${styles.dot}`} style={{ background: "#1e293b", color: "#fff" }}>2</span>
                                    <div><strong>{t("quality_policy_pdca_2_title")}</strong><div style={{ marginTop: "2px", color: "var(--gray)", fontSize: "13px" }}>{t("quality_policy_pdca_2_desc")}</div></div>
                                </li>
                                <li>
                                    <span className={`${styles.dot}`} style={{ background: "#1e293b", color: "#fff" }}>3</span>
                                    <div><strong>{t("quality_policy_pdca_3_title")}</strong><div style={{ marginTop: "2px", color: "var(--gray)", fontSize: "13px" }}>{t("quality_policy_pdca_3_desc")}</div></div>
                                </li>
                                <li>
                                    <span className={`${styles.dot}`} style={{ background: "#1e293b", color: "#fff" }}>4</span>
                                    <div><strong>{t("quality_policy_pdca_4_title")}</strong><div style={{ marginTop: "2px", color: "var(--gray)", fontSize: "13px" }}>{t("quality_policy_pdca_4_desc")}</div></div>
                                </li>
                            </ul>
                            <p style={{ fontSize: "12.5px", fontWeight: "700", color: "var(--gray)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "10px" }}>{t("quality_policy_darajalar")}</p>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "10px" }}>
                                <div style={{ background: "#f1f5f9", padding: "12px 16px", borderRadius: "8px", color: "var(--navy)" }}>
                                    <strong>{t("quality_policy_bakalavriat")}</strong>
                                </div>
                                <div style={{ background: "#f1f5f9", padding: "12px 16px", borderRadius: "8px", color: "var(--navy)" }}>
                                    <strong>{t("quality_policy_magistratura")}</strong>
                                </div>
                            </div>
                            <div style={{ background: "#ecfdf5", padding: "12px 16px", borderRadius: "8px", color: "#065f46" }}>
                                <strong>{t("quality_policy_phd")}</strong>
                            </div>
                        </div>
                    </div>

                    {/* CARD 6: SY3 Qabul */}
                    <div className={`${styles.card} ${styles.wideCard}`}>
                        <div className={`${styles.cardStrip} ${styles.tealStrip}`}></div>
                        <div className={styles.cardHeader}>
                            <div className={`${styles.cardIcon} ${styles.tealLight}`}>🎓</div>
                            <div className={styles.cardTitleArea}>
                                <div className={`${styles.cardSection} ${styles.tealText}`}>{t("quality_policy_section_6")}</div>
                                <div className={styles.cardTitle}>{t("quality_policy_admission_title")}</div>
                            </div>
                        </div>
                        <div className={styles.cardBody}>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px", marginBottom: "20px" }}>
                                <div>
                                    <p style={{ fontSize: "12px", fontWeight: "700", color: "var(--gray)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "10px" }}>{t("quality_policy_qabul_header")}</p>
                                    <ul className={styles.itemList} style={{ gap: "10px" }}>
                                        <li><span className={`${styles.dot} ${styles.tealDot}`} style={{ borderRadius: "50%", color: "#fff", background: "#0f766e", width: "18px", height: "18px", fontSize: "10px", marginTop: "2px" }}>✓</span><div style={{ alignSelf: "flex-start", fontSize: "13px", lineHeight: "1.4" }}>{t("quality_policy_qabul_item_1")}</div></li>
                                        <li><span className={`${styles.dot} ${styles.tealDot}`} style={{ borderRadius: "50%", color: "#fff", background: "#0f766e", width: "18px", height: "18px", fontSize: "10px", marginTop: "2px" }}>✓</span><div style={{ alignSelf: "flex-start", fontSize: "13px", lineHeight: "1.4" }}>{t("quality_policy_qabul_item_2")}</div></li>
                                        <li><span className={`${styles.dot} ${styles.tealDot}`} style={{ borderRadius: "50%", color: "#fff", background: "#0f766e", width: "18px", height: "18px", fontSize: "10px", marginTop: "2px" }}>✓</span><div style={{ alignSelf: "flex-start", fontSize: "13px", lineHeight: "1.4" }}>{t("quality_policy_qabul_item_3")}</div></li>
                                    </ul>
                                </div>
                                <div>
                                    <p style={{ fontSize: "12px", fontWeight: "700", color: "var(--gray)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "10px" }}>{t("quality_policy_student_centered_header")}</p>
                                    <ul className={styles.itemList} style={{ gap: "10px" }}>
                                        <li><span className={`${styles.dot} ${styles.blueDot}`} style={{ borderRadius: "50%", color: "#fff", background: "#1d4ed8", width: "18px", height: "18px", fontSize: "10px", marginTop: "2px" }}>✓</span><div style={{ alignSelf: "flex-start", fontSize: "13px", lineHeight: "1.4" }}>{t("quality_policy_student_centered_item_1")}</div></li>
                                        <li><span className={`${styles.dot} ${styles.blueDot}`} style={{ borderRadius: "50%", color: "#fff", background: "#1d4ed8", width: "18px", height: "18px", fontSize: "10px", marginTop: "2px" }}>✓</span><div style={{ alignSelf: "flex-start", fontSize: "13px", lineHeight: "1.4" }}>{t("quality_policy_student_centered_item_2")}</div></li>
                                        <li><span className={`${styles.dot} ${styles.blueDot}`} style={{ borderRadius: "50%", color: "#fff", background: "#1d4ed8", width: "18px", height: "18px", fontSize: "10px", marginTop: "2px" }}>✓</span><div style={{ alignSelf: "flex-start", fontSize: "13px", lineHeight: "1.4" }}>{t("quality_policy_student_centered_item_3")}</div></li>
                                        <li><span className={`${styles.dot} ${styles.blueDot}`} style={{ borderRadius: "50%", color: "#fff", background: "#1d4ed8", width: "18px", height: "18px", fontSize: "10px", marginTop: "2px" }}>✓</span><div style={{ alignSelf: "flex-start", fontSize: "13px", lineHeight: "1.4" }}>{t("quality_policy_student_centered_item_4")}</div></li>
                                    </ul>
                                </div>
                                <div>
                                    <p style={{ fontSize: "12px", fontWeight: "700", color: "var(--gray)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "10px" }}>{t("quality_policy_diploma_header")}</p>
                                    <ul className={styles.itemList} style={{ gap: "10px" }}>
                                        <li><span className={`${styles.dot} ${styles.navyDot}`} style={{ borderRadius: "50%", color: "#fff", background: "#1e3a8a", width: "18px", height: "18px", fontSize: "10px", marginTop: "2px" }}>✓</span><div style={{ alignSelf: "flex-start", fontSize: "13px", lineHeight: "1.4" }}>{t("quality_policy_diploma_item_1")}</div></li>
                                        <li><span className={`${styles.dot} ${styles.navyDot}`} style={{ borderRadius: "50%", color: "#fff", background: "#1e3a8a", width: "18px", height: "18px", fontSize: "10px", marginTop: "2px" }}>✓</span><div style={{ alignSelf: "flex-start", fontSize: "13px", lineHeight: "1.4" }}>{t("quality_policy_diploma_item_2")}</div></li>
                                        <li><span className={`${styles.dot} ${styles.navyDot}`} style={{ borderRadius: "50%", color: "#fff", background: "#1e3a8a", width: "18px", height: "18px", fontSize: "10px", marginTop: "2px" }}>✓</span><div style={{ alignSelf: "flex-start", fontSize: "13px", lineHeight: "1.4" }}>{t("quality_policy_diploma_item_3")}</div></li>
                                    </ul>
                                </div>
                            </div>
                            <div className={styles.badgeRow}>
                                <span className={`${styles.badgeItem} ${styles.tealLight}`}>{t("quality_policy_registrator")}</span>
                                <span className={`${styles.badgeItem} ${styles.blueLight}`}>{t("quality_policy_hemis")}</span>
                                <span className={`${styles.badgeItem} ${styles.navyLight}`}>{t("quality_policy_tyutorlar")}</span>
                                <span className={`${styles.badgeItem} ${styles.purpleLight}`}>{t("quality_policy_psixologik")}</span>
                                <span className={`${styles.badgeItem} ${styles.goldLight}`}>{t("quality_policy_karyera")}</span>
                            </div>
                        </div>
                    </div>

                    {/* CARD 7: SY4 Professor-o'qituvchilar */}
                    <div className={`${styles.card} ${styles.wideCard}`}>
                        <div className={`${styles.cardStrip} ${styles.orangeStrip}`}></div>
                        <div className={styles.cardHeader}>
                            <div className={`${styles.cardIcon}`} style={{ background: "#ffedd5", color: "#c2410c" }}>👨‍🏫</div>
                            <div className={styles.cardTitleArea}>
                                <div className={styles.cardSection} style={{ color: "var(--orange)" }}>{t("quality_policy_section_7")}</div>
                                <div className={styles.cardTitle}>{t("quality_policy_prof_resurs")}</div>
                            </div>
                        </div>
                        <div className={styles.cardBody}>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "24px", marginBottom: "10px" }}>
                                <div>
                                    <p style={{ fontSize: "12px", fontWeight: "700", color: "var(--gray)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "10px" }}>{t("quality_policy_kadrlar_tamoyillari")}</p>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "16px" }}>
                                        <div style={{ background: "#fff7ed", padding: "10px", borderRadius: "8px" }}>
                                            <strong style={{ color: "#c2410c", display: "block", fontSize: "13px" }}>{t("quality_policy_komplekslilik")}</strong><span style={{ fontSize: "12px", color: "#9a3412" }}>{t("quality_policy_komplekslilik_desc")}</span>
                                        </div>
                                        <div style={{ background: "#fff7ed", padding: "10px", borderRadius: "8px" }}>
                                            <strong style={{ color: "#c2410c", display: "block", fontSize: "13px" }}>{t("quality_policy_tizimlilik")}</strong><span style={{ fontSize: "12px", color: "#9a3412" }}>{t("quality_policy_tizimlilik_desc")}</span>
                                        </div>
                                        <div style={{ background: "#fff7ed", padding: "10px", borderRadius: "8px" }}>
                                            <strong style={{ color: "#c2410c", display: "block", fontSize: "13px" }}>{t("quality_policy_samaradorlik")}</strong><span style={{ fontSize: "12px", color: "#9a3412" }}>{t("quality_policy_samaradorlik_desc")}</span>
                                        </div>
                                        <div style={{ background: "#fff7ed", padding: "10px", borderRadius: "8px" }}>
                                            <strong style={{ color: "#c2410c", display: "block", fontSize: "13px" }}>{t("quality_policy_ochiqlik")}</strong><span style={{ fontSize: "12px", color: "#9a3412" }}>{t("quality_policy_ochiqlik_desc")}</span>
                                        </div>
                                        <div style={{ background: "#fff7ed", padding: "10px", borderRadius: "8px" }}>
                                            <strong style={{ color: "#c2410c", display: "block", fontSize: "13px" }}>{t("quality_policy_asoslilik")}</strong><span style={{ fontSize: "12px", color: "#9a3412" }}>{t("quality_policy_asoslilik_desc")}</span>
                                        </div>
                                        <div style={{ background: "#fff7ed", padding: "10px", borderRadius: "8px" }}>
                                            <strong style={{ color: "#c2410c", display: "block", fontSize: "13px" }}>{t("quality_policy_meritokratiya")}</strong><span style={{ fontSize: "12px", color: "#9a3412" }}>{t("quality_policy_meritokratiya_desc")}</span>
                                        </div>
                                    </div>
                                    <p style={{ fontSize: "12px", fontWeight: "700", color: "var(--gray)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "10px" }}>{t("quality_policy_kadrlar_siyosati")}</p>
                                    <ul className={styles.itemList} style={{ gap: "10px" }}>
                                        <li><span className={`${styles.dot}`} style={{ borderRadius: "50%", color: "#fff", background: "#c2410c", width: "18px", height: "18px", fontSize: "10px", marginTop: "2px" }}>✓</span><div style={{ alignSelf: "flex-start", fontSize: "13px", lineHeight: "1.4" }}>{t("quality_policy_kadrlar_item_1")}</div></li>
                                        <li><span className={`${styles.dot}`} style={{ borderRadius: "50%", color: "#fff", background: "#c2410c", width: "18px", height: "18px", fontSize: "10px", marginTop: "2px" }}>✓</span><div style={{ alignSelf: "flex-start", fontSize: "13px", lineHeight: "1.4" }}>{t("quality_policy_kadrlar_item_2")}</div></li>
                                        <li><span className={`${styles.dot}`} style={{ borderRadius: "50%", color: "#fff", background: "#c2410c", width: "18px", height: "18px", fontSize: "10px", marginTop: "2px" }}>✓</span><div style={{ alignSelf: "flex-start", fontSize: "13px", lineHeight: "1.4" }}>{t("quality_policy_kadrlar_item_3")}</div></li>
                                        <li><span className={`${styles.dot}`} style={{ borderRadius: "50%", color: "#fff", background: "#c2410c", width: "18px", height: "18px", fontSize: "10px", marginTop: "2px" }}>✓</span><div style={{ alignSelf: "flex-start", fontSize: "13px", lineHeight: "1.4" }}>{t("quality_policy_kadrlar_item_4")}</div></li>
                                        <li><span className={`${styles.dot}`} style={{ borderRadius: "50%", color: "#fff", background: "#c2410c", width: "18px", height: "18px", fontSize: "10px", marginTop: "2px" }}>✓</span><div style={{ alignSelf: "flex-start", fontSize: "13px", lineHeight: "1.4" }}>{t("quality_policy_kadrlar_item_5")}</div></li>
                                    </ul>
                                </div>
                                <div>
                                    <p style={{ fontSize: "12px", fontWeight: "700", color: "var(--gray)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "10px" }}>{t("quality_policy_resurslar")}</p>
                                    <ul className={styles.itemList} style={{ gap: "16px", marginBottom: "20px" }}>
                                        <li>
                                            <span style={{ background: "#1e3a8a", width: "24px", height: "24px", borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center", fontSize: "12px", color: "#fff", marginTop: "2px" }}>🏫</span>
                                            <div><strong style={{ display: "block", color: "#1e3a8a", marginBottom: "2px" }}>{t("quality_policy_infratuzilma")}</strong><div style={{ fontSize: "13px", color: "var(--gray)", lineHeight: "1.4" }}>{t("quality_policy_infratuzilma_desc")}</div></div>
                                        </li>
                                        <li>
                                            <span style={{ background: "#0f766e", width: "24px", height: "24px", borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center", fontSize: "12px", color: "#fff", marginTop: "2px" }}>📚</span>
                                            <div><strong style={{ display: "block", color: "#0f766e", marginBottom: "2px" }}>{t("quality_policy_kutubxona")}</strong><div style={{ fontSize: "13px", color: "var(--gray)", lineHeight: "1.4" }}>{t("quality_policy_kutubxona_desc")}</div></div>
                                        </li>
                                        <li>
                                            <span style={{ background: "#2563eb", width: "24px", height: "24px", borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center", fontSize: "12px", color: "#fff", marginTop: "2px" }}>💻</span>
                                            <div><strong style={{ display: "block", color: "#2563eb", marginBottom: "2px" }}>{t("quality_policy_lms")}</strong><div style={{ fontSize: "13px", color: "var(--gray)", lineHeight: "1.4" }}>{t("quality_policy_lms_desc")}</div></div>
                                        </li>
                                    </ul>
                                    <p style={{ fontSize: "12px", fontWeight: "700", color: "var(--gray)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "10px" }}>{t("quality_policy_akademik_support")}</p>
                                    <div className={styles.badgeRow}>
                                        <span className={`${styles.badgeItem} ${styles.navyLight}`}>{t("quality_policy_registrator")}</span>
                                        <span className={`${styles.badgeItem} ${styles.tealLight}`}>{t("quality_policy_uslubiy")}</span>
                                        <span className={`${styles.badgeItem} ${styles.blueLight}`}>{t("quality_policy_raqamli")}</span>
                                        <span className={`${styles.badgeItem} ${styles.purpleLight}`}>{t("quality_policy_tyutor_bolim")}</span>
                                        <span className={`${styles.badgeItem} ${styles.greenLight}`}>{t("quality_policy_yoshlar")}</span>
                                        <span className={`${styles.badgeItem} ${styles.orangeLight}`}>{t("quality_policy_psixolog")}</span>
                                        <span className={`${styles.badgeItem} ${styles.goldLight}`}>{t("quality_policy_karyera")}</span>
                                        <span className={`${styles.badgeItem} ${styles.navyLight}`}>{t("quality_policy_xalqaro")}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CARD 8: SY5 Axborot */}
                    <div className={`${styles.card} ${styles.wideCard}`}>
                        <div className={`${styles.cardStrip} ${styles.blueStrip}`}></div>
                        <div className={styles.cardHeader}>
                            <div className={`${styles.cardIcon}`} style={{ background: "#eff6ff", color: "#1e3a8a" }}>📡</div>
                            <div className={styles.cardTitleArea}>
                                <div className={`${styles.cardSection} ${styles.blueText}`}>{t("quality_policy_section_8")}</div>
                                <div className={styles.cardTitle}>{t("quality_policy_axborot_title")}</div>
                            </div>
                        </div>
                        <div className={styles.cardBody}>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "24px" }}>
                                <div>
                                    <p style={{ fontSize: "12px", fontWeight: "700", color: "var(--gray)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "10px" }}>{t("quality_policy_axborot_tizimlari")}</p>
                                    <div className={styles.miniGrid} style={{ marginBottom: "14px" }}>
                                        <div className={`${styles.miniCard} ${styles.navyLight}`}><strong>HEMIS</strong>{t("quality_policy_hemis_desc")}</div>
                                        <div className={`${styles.miniCard} ${styles.blueLight}`}><strong>LMS portal</strong>{t("quality_policy_lms_portal_desc")}</div>
                                        <div className={`${styles.miniCard} ${styles.tealLight}`}><strong>KPI platforma</strong>{t("quality_policy_kpi_platform_desc")}</div>
                                        <div className={`${styles.miniCard} ${styles.purpleLight}`}><strong>Monitoring-test</strong>{t("quality_policy_monitoring_test_desc")}</div>
                                        <div className={`${styles.miniCard} ${styles.greenLight}`}><strong>Elektron kutubxona</strong>{t("quality_policy_e_library_desc")}</div>
                                        <div className={`${styles.miniCard} ${styles.orangeLight}`}><strong>«1S Buxgalteriya»</strong>{t("quality_policy_1c_desc")}</div>
                                    </div>
                                </div>
                                <div>
                                    <p style={{ fontSize: "12px", fontWeight: "700", color: "var(--gray)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "10px" }}>{t("quality_policy_yillik_sorovnomalar")}</p>
                                    <ul className={styles.itemList} style={{ gap: "10px" }}>
                                        <li>
                                            <span style={{ color: "#1e3a8a", background: "#eff6ff", padding: "4px", borderRadius: "6px", fontSize: "12px" }}>📋</span>
                                            <div><strong style={{ color: "#1e3a8a" }}>{t("quality_policy_sorovnoma_talabalar")}</strong><div style={{ fontSize: "13px", color: "var(--gray)", marginTop: "2px" }}>{t("quality_policy_sorovnoma_talabalar_desc")}</div></div>
                                        </li>
                                        <li>
                                            <span style={{ color: "#1e3a8a", background: "#eff6ff", padding: "4px", borderRadius: "6px", fontSize: "12px" }}>📋</span>
                                            <div><strong style={{ color: "#1e3a8a" }}>{t("quality_policy_sorovnoma_prof")}</strong><div style={{ fontSize: "13px", color: "var(--gray)", marginTop: "2px" }}>{t("quality_policy_sorovnoma_prof_desc")}</div></div>
                                        </li>
                                        <li>
                                            <span style={{ color: "#1e3a8a", background: "#eff6ff", padding: "4px", borderRadius: "6px", fontSize: "12px" }}>📋</span>
                                            <div><strong style={{ color: "#1e3a8a" }}>{t("quality_policy_sorovnoma_bitiruvchilar")}</strong><div style={{ fontSize: "13px", color: "var(--gray)", marginTop: "2px" }}>{t("quality_policy_sorovnoma_bitiruvchilar_desc")}</div></div>
                                        </li>
                                        <li>
                                            <span style={{ color: "#1e3a8a", background: "#eff6ff", padding: "4px", borderRadius: "6px", fontSize: "12px" }}>📋</span>
                                            <div><strong style={{ color: "#1e3a8a" }}>{t("quality_policy_sorovnoma_ishberuvchilar")}</strong><div style={{ fontSize: "13px", color: "var(--gray)", marginTop: "2px" }}>{t("quality_policy_sorovnoma_ishberuvchilar_desc")}</div></div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CARD 9: Veb-sayt axborot siyosati */}
                    <div className={styles.card}>
                        <div className={`${styles.cardStrip} ${styles.greenStrip}`}></div>
                        <div className={styles.cardHeader}>
                            <div className={`${styles.cardIcon}`} style={{ background: "#ecfdf5", color: "#059669" }}>🌐</div>
                            <div className={styles.cardTitleArea}>
                                <div className={styles.cardSection} style={{ color: "var(--green)" }}>{t("quality_policy_section_8_veb")}</div>
                                <div className={styles.cardTitle}>{t("quality_policy_vebsayt_title")}</div>
                            </div>
                        </div>
                        <div className={styles.cardBody}>
                            <div className={`${styles.highlightInfo}`} style={{ background: "#f0fdf4", color: "var(--gray)", border: "1px solid #dcfce7", marginBottom: "16px" }}>
                                {t("quality_policy_vebsayt_guarantee")}
                            </div>
                            <p style={{ fontSize: "12px", fontWeight: "700", color: "var(--gray)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "10px" }}>{t("quality_policy_vebsayt_bolimlari")}</p>
                            <div className={styles.badgeRow} style={{ marginBottom: "16px" }}>
                                <span className={`${styles.badgeItem} ${styles.navyLight}`}>{t("navbar_universitet")}</span>
                                <span className={`${styles.badgeItem} ${styles.blueLight}`}>{t("navbar_faoliyat")}</span>
                                <span className={`${styles.badgeItem} ${styles.tealLight}`}>{t("navbar_hamkorlik")}</span>
                                <span className={`${styles.badgeItem} ${styles.greenLight}`}>{t("navbar_talabalarga")}</span>
                                <span className={`${styles.badgeItem} ${styles.purpleLight}`}>{t("navbar_qabul")}</span>
                                <span className={`${styles.badgeItem}`} style={{ background: "#ffedd5", color: "#c2410c" }}>{t("navbar_axborot_xizmati")}</span>
                            </div>
                            <ul className={styles.itemList} style={{ gap: "10px" }}>
                                <li><span className={`${styles.dot}`} style={{ borderRadius: "50%", color: "#fff", background: "#15803d", width: "18px", height: "18px", fontSize: "10px", marginTop: "2px" }}>✓</span><div style={{ alignSelf: "flex-start", fontSize: "13px", lineHeight: "1.4" }}>{t("quality_policy_axborot_xavfsizligi")}</div></li>
                                <li><span className={`${styles.dot}`} style={{ borderRadius: "50%", color: "#fff", background: "#15803d", width: "18px", height: "18px", fontSize: "10px", marginTop: "2px" }}>✓</span><div style={{ alignSelf: "flex-start", fontSize: "13px", lineHeight: "1.4" }}>{t("quality_policy_samaradorlik_eval")}</div></li>
                                <li><span className={`${styles.dot}`} style={{ borderRadius: "50%", color: "#fff", background: "#15803d", width: "18px", height: "18px", fontSize: "10px", marginTop: "2px" }}>✓</span><div style={{ alignSelf: "flex-start", fontSize: "13px", lineHeight: "1.4" }}>{t("quality_policy_rektor_report")}</div></li>
                            </ul>
                        </div>
                    </div>

                    {/* CARD 10: Yakuniy - statistika */}
                    <div className={`${styles.card} ${styles.wideCard}`}>
                        <div className={`${styles.cardStrip} ${styles.navyStrip}`}></div>
                        <div className={styles.cardHeader}>
                            <div className={`${styles.cardIcon}`} style={{ background: "#fffbeb", color: "#d97706" }}>🏆</div>
                            <div className={styles.cardTitleArea}>
                                <div className={`${styles.cardSection} ${styles.navyText}`}>{t("quality_policy_section_9")}</div>
                                <div className={styles.cardTitle}>{t("quality_policy_yakuniy_title")}</div>
                            </div>
                        </div>
                        <div className={styles.cardBody}>
                            <div className={styles.statRow} style={{ marginBottom: "20px" }}>
                                <div className={`${styles.statBox} ${styles.blueLight}`}>
                                    <div className={`${styles.statNum} ${styles.blueText}`}>11</div>
                                    <div className={`${styles.statLabel} ${styles.blueText}`}>{t("quality_policy_stat_sifat_tamoyili")}</div>
                                </div>
                                <div className={`${styles.statBox} ${styles.greenLight}`}>
                                    <div className={`${styles.statNum}`} style={{ color: "#166534" }}>5</div>
                                    <div className={`${styles.statLabel}`} style={{ color: "#166534" }}>{t("quality_policy_stat_sy_yo_nalishi")}</div>
                                </div>
                                <div className={`${styles.statBox} ${styles.navyLight}`}>
                                    <div className={`${styles.statNum} ${styles.navyText}`}>6</div>
                                    <div className={`${styles.statLabel} ${styles.navyText}`}>{t("quality_policy_stat_javobgarlik")}</div>
                                </div>
                                <div className={`${styles.statBox}`} style={{ background: "#fefce8" }}>
                                    <div className={`${styles.statNum}`} style={{ color: "#854d0e" }}>4</div>
                                    <div className={`${styles.statLabel}`} style={{ color: "#854d0e" }}>{t("quality_policy_stat_yillik_sorovnoma")}</div>
                                </div>
                                <div className={`${styles.statBox} ${styles.purpleLight}`}>
                                    <div className={styles.statNum} style={{ color: "var(--purple)" }}>10</div>
                                    <div className={styles.statLabel} style={{ color: "var(--purple)" }}>{t("quality_policy_stat_axborot_tizimi")}</div>
                                </div>
                            </div>
                            <div style={{ background: "#0f172a", borderRadius: "12px", padding: "18px 22px", color: "var(--white)" }}>
                                <strong style={{ display: "block", fontSize: "14px", marginBottom: "8px", color: "#ef4444" }}>{t("quality_policy_final_rule_header")}</strong>
                                <span style={{ fontSize: "13.5px", opacity: 0.9, lineHeight: 1.6, display: "block" }}>
                                    {t("quality_policy_final_rule_body")}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* SCROLLING IMAGES SECTION - Available for all languages */}
                <div style={{ marginTop: "60px", width: "100%" }} data-aos="fade-up">
                    <Swiper
                        modules={[Autoplay, Pagination, Navigation]}
                        spaceBetween={30}
                        slidesPerView={1}
                        breakpoints={{
                            640: { slidesPerView: 1 },
                            768: { slidesPerView: 2 },
                            1024: { slidesPerView: 3 },
                        }}
                        autoplay={{ delay: 2500, disableOnInteraction: false }}
                        pagination={{ clickable: true }}
                        navigation={true}
                        style={{ paddingBottom: "60px", paddingLeft: "10px", paddingRight: "10px" }}
                    >
                        {presentations && presentations.length > 0 ? (
                            presentations.map((item, index) => (
                                <SwiperSlide key={index}>
                                    <div style={{
                                        width: "100%",
                                        display: "flex",
                                        justifyContent: "center",
                                        padding: "10px 0"
                                    }}>
                                        <img
                                            src={item.image}
                                            alt={`Taqdimot ${index + 1}`}
                                            style={{
                                                width: "100%",
                                                height: "auto",
                                                objectFit: "contain",
                                                cursor: "pointer",
                                                borderRadius: "16px",
                                                boxShadow: "0 4px 15px rgba(0,0,0,0.12)"
                                            }}
                                            onClick={() => setSelectedImage(item.image)}
                                            onError={(e) => { e.target.src = 'https://via.placeholder.com/800x400?text=Rasm+Topilmadi'; }}
                                        />
                                    </div>
                                </SwiperSlide>
                            ))
                        ) : (
                            [1, 2, 3, 4].map((item, index) => (
                                <SwiperSlide key={index}>
                                    <div style={{
                                        width: "100%",
                                        display: "flex",
                                        justifyContent: "center",
                                        padding: "10px 0"
                                    }}>
                                        <img
                                            src={`https://picsum.photos/seed/${item * 10}/800/400`}
                                            alt={`Namuna ${item}`}
                                            style={{
                                                width: "100%",
                                                height: "auto",
                                                objectFit: "cover",
                                                cursor: "pointer",
                                                borderRadius: "16px",
                                                boxShadow: "0 4px 15px rgba(0,0,0,0.12)"
                                            }}
                                            onClick={() => setSelectedImage(`https://picsum.photos/seed/${item * 10}/800/400`)}
                                            onError={(e) => { e.target.src = 'https://via.placeholder.com/800x400?text=Rasm+Topilmadi'; }}
                                        />
                                    </div>
                                </SwiperSlide>
                            ))
                        )}
                    </Swiper>
                </div>


                {/* Admin Edit Modal */}
                {isEditModalOpen && (
                    <div className={styles.modalOverlay}>
                        <div className={styles.modalContent}>
                            <div className={styles.modalHeader}>
                                <h2>Siyosatni tahrirlash (Rich Text)</h2>
                                <button className={styles.closeBtn} onClick={() => setIsEditModalOpen(false)}>&times;</button>
                            </div>

                            <div className={styles.formList}>
                                <div className={styles.formItem} style={{ borderBottom: '2px solid #eee', paddingBottom: '20px', marginBottom: '20px' }}>
                                    <div className={styles.inputGroup}>
                                        <label style={{ fontWeight: 'bold', color: '#003366' }}>Kirish matni (Rich Text)</label>
                                        <ReactQuill
                                            theme="snow"
                                            value={introData.content_uz}
                                            onChange={updateIntro}
                                            modules={quillModules}
                                        />
                                    </div>
                                </div>

                                <label style={{ fontWeight: 'bold', color: '#003366', display: 'block', marginBottom: '10px' }}>Bo'limlar</label>

                                {policyData.map((item, index) => (
                                    <div key={index} className={styles.formItem}>
                                        <div className={styles.inputGroup}>
                                            <label>Sarlavha</label>
                                            <input
                                                className={styles.inputField}
                                                value={item.title}
                                                onChange={(e) => updateSection(index, 'title', e.target.value)}
                                            />
                                        </div>
                                        <div className={styles.inputGroup}>
                                            <label>Mazmun (Rich Text)</label>
                                            <ReactQuill
                                                theme="snow"
                                                value={item.content}
                                                onChange={(val) => updateSection(index, 'content', val)}
                                                modules={quillModules}
                                            />
                                        </div>
                                        <div className={styles.itemActions}>
                                            <button className={styles.deleteBtn} onClick={() => deleteSection(index)}>
                                                <FaTrash style={{ marginRight: '5px' }} /> O'chirish
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <button className={styles.addBtn} onClick={addNewSection}>
                                <FaPlus /> Yangi bo'lim qo'shish
                            </button>

                            <div className={styles.footerActions}>
                                <button className={styles.saveBtn} onClick={handleSave}>O'zgarishlarni saqlash</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* FULL SCREEN IMAGE MODAL */}
                {selectedImage && (
                    <div
                        style={{
                            position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
                            backgroundColor: "rgba(0,0,0,0.9)", zIndex: 9999,
                            display: "flex", justifyContent: "center", alignItems: "center", cursor: "zoom-out"
                        }}
                        onClick={() => setSelectedImage(null)}
                    >
                        <img
                            src={selectedImage}
                            style={{ maxWidth: "90%", maxHeight: "90%", objectFit: "contain" }}
                            alt="Kattalashtirilgan rasm"
                        />
                        <button
                            style={{
                                position: "absolute", top: "20px", right: "30px", background: "transparent",
                                color: "white", border: "none", fontSize: "40px", cursor: "pointer"
                            }}
                            onClick={() => setSelectedImage(null)}
                        >
                            &times;
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SifatSiyosati;
